import { z } from 'zod';
import { languages, type Language } from '../enums.js';

/**
 * Textes du courriel qui accompagne une facture, par langue : l'objet et le corps.
 *
 * Même contrat que le rappel de rendez-vous. Le courriel part dans la langue du patient ; une
 * langue sans rédaction reprend le texte du produit, déjà traduit. Du texte brut, jamais du HTML :
 * le serveur échappe tout au rendu. Un champ nul vaut « texte par défaut ».
 *
 * Les marqueurs sont ceux du gabarit du produit — `{patientName}`, `{number}`, `{amount}` — plus
 * `{organization}`, le nom du cabinet. Un seul vocabulaire. La pièce PDF est jointe par l'envoi,
 * elle n'est pas une variable.
 */

/** Variables insérables, les mêmes dans les trois langues. */
export const invoiceEmailVariables = ['patientName', 'number', 'amount', 'organization'] as const;
export type InvoiceEmailVariable = (typeof invoiceEmailVariables)[number];

export const INVOICE_EMAIL_SUBJECT_MAX_LENGTH = 200;
export const INVOICE_EMAIL_BODY_MAX_LENGTH = 4000;
export const INVOICE_EMAIL_BODY_MAX_LINES = 40;

/**
 * Tout ce qui ressemble à un marqueur. Plus large que le remplissage (`\w+`) : `{numéro}` doit
 * être signalé comme inconnu, pas partir tel quel parce que l'accent l'aurait fait passer.
 */
const MARKER = /\{([^{}\s]{1,40})\}/gu;

const KNOWN: ReadonlySet<string> = new Set(invoiceEmailVariables);

/** Marqueurs inconnus d'un texte, sans doublon, dans l'ordre d'apparition. */
export function unknownInvoiceEmailVariables(text: string): string[] {
  const unknown: string[] = [];
  for (const match of text.matchAll(MARKER)) {
    const name = match[1] ?? '';
    if (!KNOWN.has(name) && !unknown.includes(name)) unknown.push(name);
  }
  return unknown;
}

/** Retours unifiés, bords rognés. Sert à comparer une saisie au texte par défaut. */
export function normalizeInvoiceEmailText(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
}

/** Aucun caractère de contrôle hormis le retour à la ligne. */
function printable(value: string): boolean {
  return Array.from(value).every((char) => {
    const code = char.charCodeAt(0);
    return char === '\n' || (code >= 0x20 && code !== 0x7f);
  });
}

interface FieldRules {
  readonly max: number;
  readonly maxLines: number;
}

/**
 * Un champ : normalisé, borné, puis — pour l'enregistrement seulement — vérifié marqueur par
 * marqueur. La chaîne vide vaut « texte par défaut », donc `null`.
 */
function textField(rules: FieldRules, checkVariables: boolean) {
  const bounded = z
    .string()
    .max(rules.max)
    .refine((value) => value.split('\n').length <= rules.maxLines, {
      message: 'Trop de lignes',
    })
    .refine(printable, { message: 'Caractère de contrôle interdit' });
  const checked = checkVariables
    ? bounded.superRefine((value, ctx) => {
        const unknown = unknownInvoiceEmailVariables(value);
        if (unknown.length > 0) {
          ctx.addIssue({
            code: 'custom',
            message: `Variable inconnue : ${unknown.map((name) => `{${name}}`).join(', ')}`,
          });
        }
      })
    : bounded;
  return z
    .string()
    .transform(normalizeInvoiceEmailText)
    .pipe(checked)
    .transform((value) => (value === '' ? null : value))
    .nullable();
}

function textsSchema(checkVariables: boolean) {
  return z
    .object({
      subject: textField({ max: INVOICE_EMAIL_SUBJECT_MAX_LENGTH, maxLines: 1 }, checkVariables),
      body: textField(
        { max: INVOICE_EMAIL_BODY_MAX_LENGTH, maxLines: INVOICE_EMAIL_BODY_MAX_LINES },
        checkVariables,
      ),
    })
    .strict();
}

/** Textes d'une langue à l'enregistrement : une variable inconnue est refusée. */
export const invoiceEmailTextsSchema = textsSchema(true);
export type InvoiceEmailTexts = z.infer<typeof invoiceEmailTextsSchema>;

/**
 * Textes pour l'aperçu : mêmes bornes, mais une variable inconnue passe et se voit telle quelle.
 * L'aperçu se rafraîchit pendant la frappe ; l'écran signale déjà l'erreur sous le champ.
 */
export const invoiceEmailDraftTextsSchema = textsSchema(false);

/** Modèle complet : une entrée par langue, toutes facultatives (absente = textes par défaut). */
export const invoiceEmailTemplateSchema = z
  .object({
    fr: invoiceEmailTextsSchema,
    de: invoiceEmailTextsSchema,
    en: invoiceEmailTextsSchema,
  } satisfies Record<Language, typeof invoiceEmailTextsSchema>)
  .partial()
  .strict();
export type InvoiceEmailTemplate = z.infer<typeof invoiceEmailTemplateSchema>;

/** `PUT /api/organizations/current/invoice-email/template` : remplace le modèle entier. */
export const invoiceEmailTemplateUpdateSchema = z
  .object({ template: invoiceEmailTemplateSchema })
  .strict();
export type InvoiceEmailTemplateUpdateInput = z.infer<typeof invoiceEmailTemplateUpdateSchema>;

/** Aperçu : les textes non enregistrés d'une langue. */
export const invoiceEmailPreviewSchema = z
  .object({
    language: z.enum(languages),
    texts: invoiceEmailDraftTextsSchema,
  })
  .strict();
export type InvoiceEmailPreviewInput = z.infer<typeof invoiceEmailPreviewSchema>;

/** Textes par défaut d'une langue, tels que le produit les écrirait. */
export interface InvoiceEmailDefaultTexts {
  subject: string;
  body: string;
}

export interface InvoiceEmailTemplateDto {
  /** Ce que le cabinet a écrit ; `null` = texte par défaut. Les trois langues, toujours. */
  template: Record<Language, InvoiceEmailTexts>;
  defaults: Record<Language, InvoiceEmailDefaultTexts>;
}

/** Rendu du vrai courriel, avec une facture d'exemple. Aucune donnée de patient. */
export interface InvoiceEmailPreviewDto {
  subject: string;
  html: string;
  text: string;
}
